import { CalendarDays, Share2 } from "lucide-react";
import { KiraScoreVisual } from "@/components/site/KiraScoreVisual";
import { RecommendationBadge } from "@/components/site/RecommendationBadge";

function StatCell({ label, value, highlight = false }) {
  if (!value) return null;
  return (
    <div className="border-r border-kira-border last:border-r-0 px-4 py-3 first:pl-0">
      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-kira-textMuted/70 mb-1">
        {label}
      </p>
      <p className={`text-sm font-medium ${highlight ? "text-kira-gold" : "text-white"}`}>{value}</p>
    </div>
  );
}

/** Top identity card: logo, name, badges, KIRA score dial, share button and the stat strip. */
export function ReportHeader({ report, logo, onShare }) {
  return (
    <div className="rounded-xl border border-kira-border bg-gradient-to-br from-kira-surface to-black overflow-hidden mb-6">
      <div className="p-6 sm:p-8">
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-6">
          <div className="flex items-start gap-4 min-w-0">
            <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-lg bg-white/5 border border-kira-border overflow-hidden">
              {logo ? (
                <img src={logo} alt={report.company_name} className="h-full w-full object-cover" />
              ) : (
                <span className="font-chivo font-bold text-2xl text-kira-textMuted">
                  {report.company_name.charAt(0)}
                </span>
              )}
            </div>
            <div className="min-w-0">
              <h1 className="font-chivo text-3xl sm:text-4xl font-bold leading-tight">{report.company_name}</h1>
              <div className="flex flex-wrap items-center gap-2.5 mt-3">
                {report.symbol && (
                  <span className="rounded border border-kira-gold/30 bg-kira-gold/5 px-2.5 py-1 text-xs font-mono font-semibold text-kira-gold">
                    {report.symbol}
                  </span>
                )}
                <RecommendationBadge recommendation={report.recommendation} size="lg" />
                <span className="inline-flex items-center gap-1.5 text-xs font-mono text-kira-textMuted">
                  <CalendarDays className="h-3.5 w-3.5" /> {report.report_date}
                </span>
              </div>
            </div>
          </div>
          <div className="flex flex-col items-start md:items-end gap-3 shrink-0">
            <KiraScoreVisual score={report.kira_score} size={128} variant="hero" />
            <button
              data-testid="report-share-button"
              onClick={onShare}
              className="inline-flex items-center gap-1.5 rounded-md border border-kira-border px-3 py-1.5 text-xs font-medium text-kira-textMuted hover:text-kira-gold hover:border-kira-gold/50 transition-colors"
            >
              <Share2 className="h-3.5 w-3.5" /> Share
            </button>
          </div>
        </div>
      </div>

      {/* Stat strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 border-t border-kira-border">
        <StatCell label="Sector" value={report.sector} />
        <StatCell label="Industry" value={report.industry} />
        <StatCell label="Current Price" value={report.current_price} highlight />
        <StatCell label="Market Cap" value={report.market_cap} highlight />
        <StatCell label="Recommendation" value={report.recommendation} />
      </div>
    </div>
  );
}
